// Angular
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { EMPTY, Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
// Store
import { Store } from '@ngxs/store';
import {
	CleanQualityOrder,
	SetStateSelectedOrder,
	SetOrderDetailModel
} from '../state/actions/quality.actions';
import { SetPDFAttachments } from '../pdf-review/state/action/pdf-attachment.actions';
import { SetOrderDetailPipelines } from '../state/actions/quality-order-detail.actions';
// Service
import { SystemApiService } from '@cv-invision/core';
// Model
import { SKIP_AUTH_HEADER } from '@cv-invision/core';
import { OrderDetailModel } from '../state/model/order-detail.model';
import {
	DOCUMENT_ATTACHMENT_CATEGORIES,
	DOCUMENT_RULES_UI_SECTIONS,
	ORDER_STATE
} from '../model/quality.constant';
import { HistoricalOrderDetailResponse } from '../overview/model/historical-order-detail.model';
import { UcdpFindingsModel, UcdpHardStopsCodesModel, UcdpHardstopsModel, UcdpSubmissionModel } from '../model/ucdp.model';
// Other

@Injectable({ providedIn: 'root' })
export class QualityDocumentService {
	private _cachedPDF: { id: number; blob: Blob; forceReload?: boolean };
	private _loadingPDF: boolean;

	constructor(
		private store: Store,
		private systemApiService: SystemApiService,
		private httpClient: HttpClient
	) {}

	/**
	 * PUT - Approve an Order
	 * @param orderId
	 */
	approveOrder(orderId: number): Observable<any> {
		return this.httpClient
			.put(`${this.systemApiService.api['intelligenceService']}orders/${orderId}/approve`, null)
			.pipe(
				map((payload: any) => {
					if (payload.isValid) {
						this.store.dispatch(new SetStateSelectedOrder(ORDER_STATE.APPROVED));
					}
					return payload;
				})
			);
	}

	/**
	 * clean quality store
	 */
	cleanQualityStore(): any {
		return this.store.dispatch(new CleanQualityOrder());
	}

	/**
	 * PUT - Rejects the Order
	 * @param orderId
	 */
	rejectOrder(orderId: number, rejectionParams: any): Observable<any> {
		return this.httpClient
			.put(`${this.systemApiService.api['intelligenceService']}orders/${orderId}/reject`, rejectionParams)
			.pipe(
				map((payload: any) => {
					if (payload.isValid) {
						this.store.dispatch(new SetStateSelectedOrder(ORDER_STATE.REJECTED));
					}
					return payload;
				})
			);
	}

	/**
	 * GET - the document related with the order
	 * @param orderId
	 * @param documentId
	 */
	getOrderDocument(orderId: number): Observable<any> {
		return this.httpClient
			.get(
				`${
					this.systemApiService.api['intelligenceService']
				}orders/${orderId}/documents/current?includeMetadata=false`
			)
			.pipe(
				map(payload => {
					return payload;
				})
			);
	}

	/**
	 * GET - the schema related with the order
	 * @param name
	 * @param documentId
	 */
	getOrderSchema(name: string): Observable<any> {
		return this.httpClient
			.get(`${this.systemApiService.api['intelligenceService']}orders/schemas?schema=${name}`)
			.pipe(
				map(payload => {
					return payload;
				})
			);
	}

	/**
	 * GET - Returns the set of rules for a particular document, can also be filtered by UI Section.
	 * @param orderId: number
	 * @param documentId: string
	 * @param uiSection: @see DOCUMENT_RULES_UI_SECTIONS
	 */
	getOrderDocumentRules(
		orderId: number,
		documentId: string,
		uiSection: DOCUMENT_RULES_UI_SECTIONS = null
	): Observable<any> {
		const url = `${
			this.systemApiService.api['intelligenceService']
		}orders/${orderId}/documents/${documentId}/rules`;
		return this.httpClient.get(uiSection ? `${url}?uiSections=${uiSection}` : url).pipe(
			map(payload => {
				return payload;
			})
		);
	}

	/**
	 * GET - summary for rejection order
	 * @param orderId
	 */
	getOrderSummary(orderId: number): Observable<any> {
		return this.httpClient
			.get(`${this.systemApiService.api['intelligenceService']}orders/${orderId}/reject/summary`)
			.pipe(
				map(payload => {
					return payload;
				})
			);
	}

	/**
	 * GET - Get and Set the Order Detail
	 * Also prepare the order to work if this is as ORDER_STATE.QUEUED
	 * @param orderId number
	 */
	setOrderToReview(orderId: number): Observable<OrderDetailModel> {
		return this.httpClient
			.get(`${this.systemApiService.api['intelligenceService']}order-details/${orderId}`)
			.pipe(
				map((orderDetailModel: any) => {
					// Can't Be on Queued, so this change the status
					if (
						!orderDetailModel.stateEnum ||
						orderDetailModel.stateEnum === ORDER_STATE.QUEUED ||
						orderDetailModel.stateEnum === ORDER_STATE.REVISION_RECEIVED
					) {
						this.setOrderStatusToWorkWith(orderId).subscribe();
						orderDetailModel.stateEnum = ORDER_STATE.INQUALITY;
					}
					this.store.dispatch(new SetOrderDetailModel(orderDetailModel));
					return orderDetailModel;
				}),
				catchError(e => {
					return of(e);
				})
			);
	}

	/**
	 * GET - set the status under review for a new order
	 * @param orderId
	 */
	setOrderStatusToWorkWith(orderId: number): Observable<any> {
		return this.httpClient
			.put(`${this.systemApiService.api['intelligenceService']}orders/${orderId}/under-review`, null)
			.pipe(
				map(response => {
					return response;
				}),
				catchError(e => {
					return of(e);
				})
			);
	}

	/**
	 * GET - get the revised field of an order
	 * @param orderId
	 */
	getRevisedFields(orderId: number): Observable<any> {
		return this.httpClient
			.get(
				`${
					this.systemApiService.api['intelligenceService']
				}orders/${orderId}/comparer-information?includeMetadata=true`
			)
			.pipe(
				map(response => {
					return response;
				}),
				catchError(e => {
					return of(e);
				})
			);
	}

	/**
	 * GET - the current attachment file related with the order
	 * @param orderId: number
	 * @param category: string
	 */
	getOrderDocumentAttachment(
		orderId: any,
		category: DOCUMENT_ATTACHMENT_CATEGORIES
	): Observable<any> {
		return this.httpClient
			.get(
				`${
					this.systemApiService.api['intelligenceService']
				}orders/${orderId}/documents/current/attachments?category=${category}`
			)
			.pipe(
				map((payload: any) => {
					return payload.data;
				})
			);
	}

	/**
	 * GET - get all attachment file related with the document
	 * @param orderId: number
	 * @param category: string
	 */
	getOrderAllDocumentAttachment(orderId: any, documentId: string): Observable<any> {
		return this.httpClient
			.get(
				this.systemApiService.api['intelligenceService'] +
					`orders/${orderId}/documents/${documentId}/attachments`
			)
			.pipe(
				map((payload: any) => {
					return payload.data;
				})
			);
	}

	/**
	 * GET - returns the order document attachments by category.
	 * @param orderId: number
	 * @param category: string
	 */
	getOrderDocumentAttachments(
		orderId: any,
		category: DOCUMENT_ATTACHMENT_CATEGORIES
	): Observable<any> {
		return this.httpClient
			.get(
				`${
					this.systemApiService.api['intelligenceService']
				}orders/${orderId}/attachments?category=${category}`
			)
			.pipe(map(payload => payload));
	}

	/**
	 * GET - Returns the PDF attachments for a particular order
	 * @param orderId: any
	 */
	getDocumentPDFAttachments(orderId: any): void {
		this.store.dispatch(new SetPDFAttachments(orderId));
	}

	/**
	 * Used to retrieve blob response from server api.
	 * @param path
	 * @param options
	 */
	downloadPDFFile(orderId: number, path: string): Observable<any> {
		this._loadingPDF = true;
		this._cachedPDF = { id: orderId, blob: null };
		return this.httpClient
			.get(path, {
				headers: { [SKIP_AUTH_HEADER]: SKIP_AUTH_HEADER },
				responseType: 'blob'
			})
			.pipe(
				map((response: any) => {
					const result = {
						blob: new Blob([response], { type: 'application/pdf' }),
						filename: 'pdfDoc'
					};
					this.cachePDF(orderId, result.blob);
					this._loadingPDF = false;
					return result;
				})
			);
	}

	/**
	 * Stores the PDF on cache.
	 * @param id: the orderId
	 * @param blob: Blob
	 */
	cachePDF(id: number, blob: Blob, forceReload: boolean = false): void {
		this._cachedPDF = { id: id, blob: blob, forceReload: forceReload };
	}

	/**
	 * Returns the cache stored PDF.
	 */
	loadCurrentPDF(): Blob {
		return this._cachedPDF ? this._cachedPDF.blob : null;
	}

	/**
	 * Returns current PDF cached blob.
	 */
	currentPDFFileId(): number {
		return this._cachedPDF ? this._cachedPDF.id : null;
	}

	/**
	 * Force reload for current cached PDF blob.
	 */
	forceReloadForCurrentPDF(): void {
		if (this._cachedPDF) {
			this._cachedPDF.forceReload = true;
		}
	}

	/**
	 * Does the current cached PDF is flagged to be reloaded.
	 */
	isForceReloadCurrentPDF(): boolean {
		return this._cachedPDF && this._cachedPDF.blob ? this._cachedPDF.forceReload! : true;
	}

	/**
	 * Returns true if PDF is currently loading.
	 */
	isLoadingPDF(): boolean {
		return this._loadingPDF;
	}

	/**
	 * GET - the Geocoordinates from a address string
	 * @param orderId
	 * @param documentId
	 */
	getOrderCoordinates(address: string): Observable<any> {
		return this.httpClient
			.get(
				`${
					this.systemApiService.api['intelligenceService']
				}geo-services/geocode?formattedAddress=${address}`
			)
			.pipe(
				map(payload => {
					return payload;
				})
			);
	}

	/**
	 * Used to retrieve blob response from server api.
	 * @param path
	 * @param options
	 */
	downloadFile(path: string): Observable<any> {
		return this.httpClient
			.get(path, {
				headers: { [SKIP_AUTH_HEADER]: SKIP_AUTH_HEADER },
				responseType: 'blob'
			})
			.pipe(
				map((response: any) => {
					const result = {
						blob: new Blob([response], { type: 'application/pdf' }),
						filename: 'pdfDoc'
					};
					return result;
				})
			);
	}

	/**
	 * Regenerate QRR Report
	 * @param orderId: number
	 */
	regenerateQRRReport(orderId: any): Observable<any> {
		return this.httpClient
			.put(`${this.systemApiService.api['intelligenceService']}orders/${orderId}/qrr/regenerate`, '{}')
			.pipe(
				map(payload => {
					return payload;
				})
			);
	}

	/**
	 * Generate ENV File
	 *
	 * @param orderId: any
	 * @param documentId: any
	 */
	generateENVFile(orderId: any, documentId: any): Observable<any> {
		return this.httpClient
			.post(`${this.systemApiService.api['intelligenceService']}orders/${orderId}/documents/${documentId}/exports/env`, '{}')
			.pipe(
				map(payload => {
					return payload;
				})
			);
	}

	/**
	 * Get ENV File generation status
	 *
	 * @param orderId: any
	 * @param documentId: any
	 */
	getENVfileStatus(orderId: any, documentId: any): Observable<any> {
		return this.httpClient
			.get(`${this.systemApiService.api['intelligenceService']}orders/${orderId}/documents/${documentId}/exports`)
			.pipe(
				map(payload => {
					return payload;
				})
			);
	}

	/**
	 * Get Order Subject Public Record
	 * @param orderId: number
	 */
	getOrderSubjectPublicRecord(orderId: any): Observable<any> {
		return this.httpClient
			.get(`${this.systemApiService.api['intelligenceService']}orders/${orderId}/externaldata/publicrecord`)
			.pipe(
				map(payload => {
					return payload;
				})
			);
	}

	/**
	 * Get Order Comps Public Records
	 * @param orderId: number
	 */
	getOrderCompsPublicRecord(orderId: any): Observable<any> {
		return this.httpClient
			.get(`${this.systemApiService.api['intelligenceService']}orders/${orderId}/externaldata/compublicrecord`)
			.pipe(
				map(payload => {
					return payload;
				})
			);
	}

	/**
	 * Get the list of all available PDF reports for an order
	 *
	 * @param orderId
	 * @returns
	 */
	getOrderAvailablePdf(orderId: number): Observable<any> {
		return this.httpClient
			.get(`${this.systemApiService.api['intelligenceService']}orders/${orderId}/reports`)
			.pipe(
				map(payload => {
					return payload;
				})
			);
	}

	/**
	 * Download an Available PDF report on the fly
	 * @param orderId
	 * @param key
	 * @returns
	 */
	downloadNonChachedPDFFile(orderId: number, key: string): Observable<any> {
		const url = `${this.systemApiService.api['intelligenceService']}orders/${orderId}/reports/${key}`;
		return this.httpClient
			.get(
				url,
				{
					responseType: 'blob'
				}
			)
			.pipe(
				map((response: any) => {
					const result = {
						blob: new Blob([response], { type: 'application/pdf' }),
						filename: 'pdfDoc'
					};
					return result;
				})
			);
	}

	/**
	 * POST - Lock the order for edit.
	 */
	public lockOrder(orderId: number): Observable<any> {
		return this.httpClient.post(
			`${this.systemApiService.api['intelligenceService']}orders/${orderId}/lock`,
			null
		);
	}

	/**
	 * POST - Unlock the order.
	 */
	public unlockOrder(orderId: number, isForceUnlock: boolean = false): Observable<any> {
		return this.httpClient.post(
			`${this.systemApiService.api['intelligenceService']
			}orders/${orderId}/unlock?force=${isForceUnlock}`,
			null
		);
	}

	/**
	 * PUT - Change order status to Not Reviewed
	 * @param orderId
	 * @returns
	 */
	public markOrderNotReviewed (orderId: number): Observable<any>{
		return this.httpClient.put(
			`${this.systemApiService.api['intelligenceService']}orders/${orderId}/not-reviewed`,
			null
		);
	}

	/**
	 * GET - Get the configurations for the pipeline indicators
	 */
	public getPipelineIndicatorsConfig(): Observable<any>{
		return this.httpClient
			.get(`${this.systemApiService.api['intelligenceService']}manager/pipeline-indicators`)
			.pipe(
				map((payload:any) => this.store.dispatch(new SetOrderDetailPipelines(payload.data)))
			);
	}

	/**
	 * Gets the paginated historical details from an order.
	 * @param {number} orderId - The order id
	 * @returns {Observable<HistoricalOrderDetailResponse>}
	 */
	getOrderHistoricalDetails(
		orderId: number,
		offset = 0,
		limit = 20
	): Observable<HistoricalOrderDetailResponse> {
		const params = new HttpParams()
		  .set('offset', String(offset))
		  .set('limit', String(limit));

		return this.httpClient
			.get<any>(
				this.systemApiService.api['intelligenceService'] +
					`order-details/${orderId}/tracking-status-history`,
				{ params }
			)
			.pipe(
				map((response: any) => {
					const r = response ?? {};
					const shr = r.statusHistoryResult ?? {};

					const mappedResponse: HistoricalOrderDetailResponse = {
						id: r.id ?? 0,
						orderId: r.orderId ?? 0,
						statusHistoryResult: {
							data: shr.data ?? [],
							totalCount: shr.totalCount ?? 0,
							offset: shr.offset ?? offset,
							limit: shr.limit ?? limit,
						},
						updatedDate: r.updatedDate ?? '',
					};

					return mappedResponse;
				})
			);
	}

	/**
	 * Get UCDP/EAD submission information
	 *
	 * @param orderId
	 * @returns
	 */
	getUCDPSubmissions(orderId: number): Observable<UcdpSubmissionModel> {
		const url = `${this.systemApiService.api['intelligenceService']}orders/${orderId}/qc-submissions`;

		return this.httpClient.get(url)
			.pipe(
				map((response: any) => {
					return response.key;
				})
			);
	}

	/**
	 * Post a new submission to the propper service
	 * @param orderId
	 * @param service
	 * @returns
	 */
	postUCDPSubmission(orderId: number, service: string): Observable<any> {
		const url = `${this.systemApiService.api['intelligenceService']}orders/${orderId}/qc-submissions/${service}`;

		return this.httpClient.post(url, {})
			.pipe(
				catchError(err => {
					console.warn('Ignoring error:', err);
					return of({
						...err.error,
						isValid: false
					});
				}),
				map((response: any) => {
					return response;
				})
			);
	}

	/**
	 * Get UCDP/EAD submission findings
	 *
	 * @param orderId
	 * @param submissionId
	 * @returns
	 */
	getUCDPFindings(orderId: number, submissionId: string): Observable<UcdpFindingsModel> {
		const url = `${this.systemApiService.api['intelligenceService']}orders/${orderId}/qc-submissions/${submissionId}/findings?limit=500`;

		return this.httpClient.get(url)
			.pipe(
				catchError(err => {
					console.warn('Ignoring error:', err);
					return of(new UcdpFindingsModel());
				}),
				map((response: any) => {
					if (response.isValid) {
						return response.key;
					} else {
						return new UcdpFindingsModel();
					}
				})
			);
	}

	/**
	 * Get UCDP/EAD submission hardstops
	 *
	 * @param orderId
	 * @param submissionId
	 * @returns
	 */
	getUCDPHardstops(orderId: number, submissionId: string): Observable<UcdpHardstopsModel> {
		const url = `${this.systemApiService.api['intelligenceService']}orders/${orderId}/qc-submissions/${submissionId}/failures?limit=500`;

		return this.httpClient.get(url)
			.pipe(
				catchError(err => {
					console.warn('Ignoring error:', err);
					return of(new UcdpHardstopsModel());
				}),
				map((response: any) => {
					if (response.isValid) {
						return response.key;
					} else {
						return new UcdpHardstopsModel();
					}
				})
			);
	}

	/**
	 * Get reasonCodes related to a hardStop code endpoint
	 *
	 * @param code
	 * @param service
	 * @returns
	 */
	getHardStopCodes(code: string, service: string): Observable<UcdpHardStopsCodesModel[]> {
		const url = `${this.systemApiService.api['intelligenceService']}orders/qc-submissions/${service}/reason-code/${code}`;

		return this.httpClient.get(url)
			.pipe(
				catchError(err => {
					console.warn('Ignoring error:', err);
					return of([]);
				}),
				map((response: any) => {
					if (response.isValid) {
						return response.key;
					} else {
						return [];
					}
				})
			);
	}

	/**
	 * POST the overrideReason data to api
	 *
	 * @param orderId
	 * @param submissionId
	 * @param hardstopId
	 * @param params
	 * @returns
	 */
	postReasonCode(orderId: number, submissionId: string, hardstopId: string, params: any): Observable<any> {
		const url = `${this.systemApiService.api['intelligenceService']}orders/${orderId}/qc-submissions/${submissionId}/failures/${hardstopId}`;
		const body = {
			reasonCode: params.reasonCode,
			comment: params.comment,
		};

		return this.httpClient.post(url, body)
			.pipe(
				catchError(err => {
					console.warn('Ignoring error:', err);
					return of({
						...err.error,
						isValid: false
					});
				}),
				map((response: any) => {
					return response;
				})
			);
	}

	/**
	 * POST the overrideReason data to api
	 *
	 * @param orderId
	 * @param submissionId
	 * @param hardstopId
	 * @param params
	 * @returns
	 */
	postOverridesRevision(orderId: number, submissionId: string): Observable<any> {
		const url = `${this.systemApiService.api['intelligenceService']}orders/${orderId}/qc-submissions/${submissionId}/failures`;

		return this.httpClient.post(url, {})
			.pipe(
				catchError(err => {
					console.warn('Ignoring error:', err);
					return of({
						...err.error,
						isValid: false
					});
				}),
				map((response: any) => {
					return response;
				})
			);
	}
}
